import type { ClefSelection, KeySignature, InputMode } from '../types/music';
import { usePractice } from '../context/PracticeContext';
import { Button } from './ui/Button';
import { Toggle } from './ui/Toggle';
import { Select } from './ui/Select';
import { KEY_SIGNATURE_OPTIONS } from '../utils/keySignatures';
import { TREBLE_RANGE_PRESETS, BASS_RANGE_PRESETS } from '../utils/noteMappings';
import {
  getAvailableInstruments,
  getInstrumentDisplayName
} from '../audio/instrumentEngine';

interface SettingsPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback to close the panel */
  onClose: () => void;
}

/**
 * Settings panel component
 *
 * Allows users to configure:
 * - Clef selection (treble/bass/mixed)
 * - Key signature
 * - Accidentals (naturals only, include sharps/flats)
 * - Note range for each clef
 * - Piano display toggle
 * - Input mode (click/keyboard/mixed)
 * - Audio toggle
 * - Color-coded notes toggle
 */
export function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
  const { state, updateSettings, resetSession } = usePractice();
  const { settings } = state;

  if (!isOpen) return null;

  const clefOptions: { value: ClefSelection; label: string }[] = [
    { value: 'treble', label: 'Treble Clef Only' },
    { value: 'bass', label: 'Bass Clef Only' },
    { value: 'mixed', label: 'Mixed (Both Clefs)' }
  ];

  const inputModeOptions: { value: InputMode; label: string }[] = [
    { value: 'mixed', label: 'Mixed (Click or Keyboard)' },
    { value: 'click', label: 'Click Only' },
    { value: 'keyboard', label: 'Keyboard Only' }
  ];

  const trebleRangeOptions = TREBLE_RANGE_PRESETS.map(p => ({
    value: p.id,
    label: p.label
  }));

  const bassRangeOptions = BASS_RANGE_PRESETS.map(p => ({
    value: p.id,
    label: p.label
  }));

  const handleClefChange = (clef: ClefSelection) => {
    updateSettings({ clefSelection: clef });
  };

  const handleKeySignatureChange = (key: KeySignature) => {
    updateSettings({ keySignature: key });
  };

  const handleTrebleRangeChange = (rangeId: string) => {
    const preset = TREBLE_RANGE_PRESETS.find(p => p.id === rangeId);
    if (preset) {
      updateSettings({
        trebleRange: { low: preset.low, high: preset.high, label: preset.label }
      });
    }
  };

  const handleBassRangeChange = (rangeId: string) => {
    const preset = BASS_RANGE_PRESETS.find(p => p.id === rangeId);
    if (preset) {
      updateSettings({
        bassRange: { low: preset.low, high: preset.high, label: preset.label }
      });
    }
  };

  const handleResetSession = () => {
    if (confirm('Reset all progress? This will clear your statistics and start fresh.')) {
      resetSession();
      onClose();
    }
  };

  // Find current range preset IDs
  const currentTrebleRangeId = TREBLE_RANGE_PRESETS.find(
    p => p.low === settings.trebleRange.low && p.high === settings.trebleRange.high
  )?.id || 'easy';

  const currentBassRangeId = BASS_RANGE_PRESETS.find(
    p => p.low === settings.bassRange.low && p.high === settings.bassRange.high
  )?.id || 'easy';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto animate-slide-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 id="settings-title" className="text-xl font-semibold text-gray-800">
            Settings
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close settings">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        <div className="p-6 space-y-8">
          {/* Clef Selection */}
          <section>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Clef Selection</h3>
            <Select
              value={settings.clefSelection}
              options={clefOptions}
              onChange={handleClefChange}
              label="Practice Mode"
              description="Choose which clef(s) to practice"
            />
          </section>

          {/* Key Signature */}
          <section>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Key Signature</h3>
            <Select
              value={settings.keySignature}
              options={KEY_SIGNATURE_OPTIONS}
              onChange={handleKeySignatureChange}
              label="Key"
              description="Notes will include accidentals from this key"
            />
          </section>

          {/* Accidentals */}
          <section>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Accidentals</h3>
            <div className="space-y-4">
              <Toggle
                checked={settings.naturalsOnly}
                onChange={(checked) => updateSettings({
                  naturalsOnly: checked,
                  includeSharps: checked ? false : settings.includeSharps,
                  includeFlats: checked ? false : settings.includeFlats
                })}
                label="Naturals Only"
                description="Only practice natural notes (no sharps or flats)"
              />
              <Toggle
                checked={settings.includeSharps}
                onChange={(checked) => updateSettings({
                  includeSharps: checked,
                  naturalsOnly: checked ? false : settings.naturalsOnly
                })}
                label="Include Sharps"
                description="Add sharp notes to the practice deck"
                disabled={settings.naturalsOnly}
              />
              <Toggle
                checked={settings.includeFlats}
                onChange={(checked) => updateSettings({
                  includeFlats: checked,
                  naturalsOnly: checked ? false : settings.naturalsOnly
                })}
                label="Include Flats"
                description="Add flat notes to the practice deck"
                disabled={settings.naturalsOnly}
              />
            </div>
          </section>

          {/* Note Ranges */}
          <section>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Note Ranges</h3>
            <div className="space-y-4">
              <Select
                value={currentTrebleRangeId}
                options={trebleRangeOptions}
                onChange={handleTrebleRangeChange}
                label="Treble Clef Range"
                disabled={settings.clefSelection === 'bass'}
              />
              <Select
                value={currentBassRangeId}
                options={bassRangeOptions}
                onChange={handleBassRangeChange}
                label="Bass Clef Range"
                disabled={settings.clefSelection === 'treble'}
              />
              <p className="text-xs text-gray-500">
                Note: The range will automatically expand as you improve (adaptive difficulty).
              </p>
            </div>
          </section>

          {/* Input Mode */}
          <section>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Input Mode</h3>
            <Select
              value={settings.inputMode}
              options={inputModeOptions}
              onChange={(mode) => updateSettings({ inputMode: mode })}
              label="Answer Method"
              description="How you want to identify notes"
            />
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Keyboard shortcuts:</strong><br />
                A-G keys for note names<br />
                Left Shift + key = flat<br />
                Right Shift + key = sharp
              </p>
            </div>
          </section>

          {/* Display Options */}
          <section>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Display Options</h3>
            <div className="space-y-4">
              <Toggle
                checked={settings.showPiano}
                onChange={(checked) => updateSettings({ showPiano: checked })}
                label="Show Piano Keyboard"
                description="Display a one-octave piano at the bottom"
              />
              <Toggle
                checked={settings.colorCodeNotes}
                onChange={(checked) => updateSettings({ colorCodeNotes: checked })}
                label="Color-Code Notes"
                description="Color notes by pitch class (C=red, D=orange, etc.)"
              />
            </div>
          </section>

          {/* Audio */}
          <section>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Audio</h3>
            <div className="space-y-4">
              <Toggle
                checked={settings.playAudio}
                onChange={(checked) => updateSettings({ playAudio: checked })}
                label="Play Note Sounds"
                description="Play audio when revealing cards"
              />
              <Select
                value={settings.instrumentId}
                options={getAvailableInstruments().map((id) => ({
                  value: id,
                  label: getInstrumentDisplayName(id)
                }))}
                onChange={(id) => updateSettings({ instrumentId: id })}
                label="Instrument"
                description="Select instrument sound for playback"
                disabled={!settings.playAudio}
              />
            </div>
          </section>

          {/* Reset */}
          <section className="pt-4 border-t border-gray-200">
            <Button
              variant="danger"
              onClick={handleResetSession}
              className="w-full"
            >
              Reset All Progress
            </Button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              This will clear all statistics and reset difficulty to beginner.
            </p>
          </section>
        </div>
      </div>
    </>
  );
}

/**
 * Settings button that opens the panel
 */
export function SettingsButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      aria-label="Open settings"
      className="p-2"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    </Button>
  );
}
